import React, { useMemo } from 'react';
import { useMount } from 'react-use';
import { VStack, Text, Divider } from '@chakra-ui/react';
import { useDeliveryAddressState, useModalDispatch } from '@olo-web/client-state';
import { useOrder } from '@olo-web/domain/orders/queries/useOrder';
import { useMerchant } from '@domain/merchants/queries/useMerchant';
import { useEstimateDelivery } from '@olo-web/domain/deliveryService/queries/useEstimateDelivery';
import { ContactlessDeliveryCheckbox, TextRowWithButton } from '../common';
import { EModalTypes } from '@olo-web/types/enums';
import {
  useIsDelivery,
  useEstimatedTimeText,
  useOrderTypeDisplayName,
  useIsThirdPartyDelivery,
  useIsDineIn,
  useIsScanAndPayPage,
  useOrderType,
} from '@olo-web/utils/common/hooks';
import { useScreenSizeState } from '@olo-web/client-state';
import { TextFlexRowWithChildren } from '@atoms/TextFlexRowWithChildren';
import { ContentCard } from '@olo-web/components/atoms/ContentCard';
import { OrderNote } from '../OrderNote';
import { useDeliveryModal } from '@olo-web/utils/common/hooks';
import dayjs from 'dayjs';

export const CheckoutOrderDetails = ({ showOrderNote }: { showOrderNote: boolean }) => {
  const isDelivery = useIsDelivery();
  const isThirdPartyDelivery = useIsThirdPartyDelivery();
  const { address } = useDeliveryAddressState();
  const modalDispatch = useModalDispatch();
  const { data: merchant } = useMerchant();
  const { data: order } = useOrder();
  const { isMdDown } = useScreenSizeState();
  const { data: deliveryEstimate } = useEstimateDelivery();
  const { openDeliveryModal } = useDeliveryModal();
  const orderTypeDisplayName = useOrderTypeDisplayName();
  const orderType = useOrderType();
  const now = dayjs();
  const asapOrderTimeDate = dayjs(merchant?.orderTypes?.[orderType].asapOrderDateTime);
  const isAsapOrderDateTimeToday = asapOrderTimeDate.isSame(now, 'day');
  const isDineIn = useIsDineIn();
  const isScanAndPay = useIsScanAndPayPage();

  useMount(() => {
    if (!isAsapOrderDateTimeToday && !isDineIn && !isScanAndPay && merchant?.isOrderingAvailable) {
      modalDispatch({
        type: 'OPEN_MODAL',
        payload: { modalKey: EModalTypes.CONFIRM_FULFILLMENT_TIME },
      });
    }
  });

  const addressString = useMemo(() => {
    if (isDelivery) {
      const a = address;
      if (!a) return 'Please add your delivery address';
      return `${a.address1}, ${a?.address2 ? a?.address2 + ',' : ''} ${a.city}, ${a.state} ${
        a.zip
      }`;
    }
    return `${merchant?.locationString} ${merchant?.zip}`;
  }, [address, isDelivery, merchant]);

  const estimatedTimeText = useEstimatedTimeText(
    isThirdPartyDelivery ? deliveryEstimate?.deliveryTime : order?.orderDateTime
  );

  const handleDateTimeClick = () =>
    modalDispatch({
      type: 'OPEN_MODAL',
      payload: { modalKey: EModalTypes.ORDER_TIME },
    });

  return (
    <ContentCard
      data-testid="order-details-section"
      title={`${orderTypeDisplayName} details`}
      rightTitleAdornment={isDelivery && !isMdDown && <ContactlessDeliveryCheckbox />}
    >
      <VStack spacing={4} alignItems="flex-start" justifyContent="flex-start" w="100%">
        <VStack
          spacing={2}
          mt={1}
          alignItems="flex-start"
          w="100%"
          data-testid="order-address-and-time"
        >
          {isDelivery ? (
            <TextRowWithButton
              bold="Delivering to:"
              text={addressString}
              buttonText="Edit"
              onClick={openDeliveryModal}
            />
          ) : (
            <TextFlexRowWithChildren bold="Pickup from:" text={addressString} />
          )}
          {isThirdPartyDelivery && (
            <Text textStyle="label" color="blackAlpha.400" mt="0.1rem !important">
              Your order will be delivered by DoorDash
            </Text>
          )}
          <TextRowWithButton
            bold="When:"
            text={estimatedTimeText}
            buttonText={merchant?.asapOnly ? undefined : 'Edit'}
            onClick={handleDateTimeClick}
            ariaLabel={`Edit ${isDelivery ? 'delivery' : 'pickup'} time`}
          />
          {isDelivery && isMdDown && <ContactlessDeliveryCheckbox pr={2} />}
          {isMdDown && <Divider p={1} borderColor="gray.200" />}
          {showOrderNote && <OrderNote />}
        </VStack>
      </VStack>
    </ContentCard>
  );
};
